import React, { useState } from "react";
import { Alert, Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material";

import { BASE_DATA_LAYERS } from "../layers";

import { BoundaryImportKey } from "./boundary";
import { FlyoutSubOptions } from "./FlyoutSubOptions";
import { formatImportNouns } from "./importTask";
import ReplaceConfirmation from "./ReplaceConfirmation";

interface ImportChecklistProps {
  /** Base data layers offered, in display order. */
  keys: readonly BoundaryImportKey[];
  ticked: Partial<Record<BoundaryImportKey, boolean>>;
  onToggle(key: BoundaryImportKey): void;
  /** Layers that are always imported. */
  fixedKeys?: readonly BoundaryImportKey[];
  /** Whether each layer already has data that an import replaces; fixed layers are never confirmed. */
  hasData: Partial<Record<BoundaryImportKey, boolean>>;
  /** Lead-in naming the area, shown with the checklist inside the expanded option's choices. */
  lead?: string;
  /** Why the ticked layers can't be imported, if they can't. */
  warning?: string | null;
  onImport(): void;
  onBack?(): void;
}

/**
 * Checklist of base data layers to import FOND data into, confirming replacing ticked layers that have data.
 */
const ImportChecklist: React.FC<ImportChecklistProps> = ({ keys, ticked, onToggle, fixedKeys = [], hasData, lead, warning, onImport, onBack }) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const tickedKeys = keys.filter((key) => ticked[key]);
  const replacedKeys = tickedKeys.filter((key) => hasData[key] && !fixedKeys.includes(key));

  if (isConfirming) {
    return (
      <ReplaceConfirmation
        layerNoun={formatImportNouns(replacedKeys)}
        onCancel={() => setIsConfirming(false)}
        onReplace={() => {
          setIsConfirming(false);
          onImport();
        }}
      />
    );
  }

  const checklist = (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box border={1} borderColor="divider" borderRadius={1} bgcolor="background.paper">
        {keys.map((key) => (
          <Box
            key={key}
            display="flex"
            alignItems="center"
            px={1.25}
            borderBottom={1}
            borderColor="divider"
            sx={{ "&:last-of-type": { borderBottom: 0 } }}
          >
            <FormControlLabel
              sx={{ flex: 1, m: 0 }}
              control={<Checkbox size="small" checked={!!ticked[key]} disabled={fixedKeys.includes(key)} onChange={() => onToggle(key)} />}
              label={<Typography variant="body2">{BASE_DATA_LAYERS[key].name}</Typography>}
            />
          </Box>
        ))}
      </Box>
      {warning && (
        <Alert severity="warning" variant="compact">
          {warning}
        </Alert>
      )}
      <Box display="flex" justifyContent="flex-end" gap={0.5}>
        {onBack && (
          <Button size="small" onClick={onBack}>
            Back
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          disabled={tickedKeys.length === 0 || !!warning}
          onClick={replacedKeys.length > 0 ? () => setIsConfirming(true) : onImport}
        >
          Import
        </Button>
      </Box>
    </Box>
  );

  if (!lead) return checklist;

  return (
    <FlyoutSubOptions>
      <Typography variant="caption" color="text.secondary" px={1} pt={0.5} pb={0.75}>
        {lead}
      </Typography>
      <Box px={0.5} pb={0.5}>
        {checklist}
      </Box>
    </FlyoutSubOptions>
  );
};

export default ImportChecklist;
